import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Textarea } from "design-system/atoms/Textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { cn } from "design-system/utils"
import { CircleCheck, RotateCcw } from "lucide-react"
import { useUpdateIssueResolutionMutation } from "modules/support/hooks/useUpdateIssueResolutionMutation"
import { useId, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { IssueStatusEnum, SupportUpdateIssueResolutionSchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"

type Props = {
  issue: {
    id: string
    status: (typeof IssueStatusEnum)[number]
    qualifiedAt: string | null
  }
  className?: string
}

export function IssueCloseButton({ issue, className }: Props) {
  const { t } = useTranslation()
  const { mutateAsync: updateClosure, isPending } = useUpdateIssueResolutionMutation(issue.id)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const noteId = useId()
  const form = useForm<z.input<typeof SupportUpdateIssueResolutionSchema>>({
    resolver: zodResolver(SupportUpdateIssueResolutionSchema),
    defaultValues: { resolved: true, closingNote: "" },
  })

  const isClosed = issue.status === "Closed"

  if (issue.status === "Resolved" || (!isClosed && issue.qualifiedAt === null)) return null

  const label = isClosed
    ? t("support.entities.resolution.reopen")
    : t("support.entities.resolution.close")

  async function submitClosure(props: z.input<typeof SupportUpdateIssueResolutionSchema>) {
    await updateClosure(props)
    setIsPopoverOpen(false)
    form.reset()

    toast.success(
      props.resolved
        ? t("support.entities.resolution.closedToast")
        : t("support.entities.resolution.reopenedToast"),
      {
        action: {
          label: t("shared.undo"),
          onClick: () => updateClosure({ resolved: !props.resolved }),
        },
      },
    )
  }

  return (
    <Tooltip>
      <Popover
        open={!isClosed && isPopoverOpen}
        onOpenChange={(isOpen) => {
          if (isClosed) return

          setIsPopoverOpen(isOpen)
          if (!isOpen) form.reset()
        }}
      >
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={label}
              disabled={isPending}
              onClick={async (event) => {
                event.stopPropagation()
                if (isClosed) await submitClosure({ resolved: false })
              }}
              className={cn("size-8 text-muted-foreground hover:text-foreground", className)}
            >
              {isClosed ? <RotateCcw /> : <CircleCheck />}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
        <PopoverContent onClick={(event) => event.stopPropagation()}>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.stopPropagation()
              return form.handleSubmit(submitClosure)(event)
            }}
          >
            <label htmlFor={noteId} className="text-sm font-medium">
              {t("support.entities.resolution.closingNote")}
            </label>
            <Textarea
              id={noteId}
              {...form.register("closingNote")}
              placeholder={t("support.entities.resolution.closingNotePlaceholder")}
              disabled={isPending}
            />
            <Button type="submit" disabled={isPending}>
              {t("support.entities.resolution.close")}
            </Button>
          </form>
        </PopoverContent>
      </Popover>
    </Tooltip>
  )
}
